import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import * as authUtils from '../utils/auth';
import { BACKEND_URL } from '../utils/backendUrl';
import ConfirmationModal from '../components/ConfirmationModal';

const SettingsPage = () => {
  const { user, logout } = useAuth();
  const getInitialTab = () => {
    if (typeof window === 'undefined') return 'account';
    const requestedTab = sessionStorage.getItem('craftmyparty_settings_tab');
    return ['account', 'preferences', 'privacy'].includes(requestedTab) ? requestedTab : 'account';
  };
  const [activeTab, setActiveTab] = useState(getInitialTab);

  // Handle dynamic tab navigation and section scrolling from other pages
  useEffect(() => {
    const handleNavigation = () => {
      const requestedTab = sessionStorage.getItem('craftmyparty_settings_tab');
      if (['account', 'preferences', 'privacy'].includes(requestedTab)) {
        setActiveTab(requestedTab);
        sessionStorage.removeItem('craftmyparty_settings_tab');
      } else if (requestedTab) {
        sessionStorage.removeItem('craftmyparty_settings_tab');
      }

      const requestedSection = sessionStorage.getItem('craftmyparty_settings_section');
      if (requestedSection) {
        sessionStorage.removeItem('craftmyparty_settings_section');
        // Give time for the tab content to render before scrolling
        setTimeout(() => {
          document.getElementById(requestedSection)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    };

    handleNavigation();
    window.addEventListener('focus', handleNavigation);
    return () => window.removeEventListener('focus', handleNavigation);
  }, []);
  const [deleteAccountConfirm, setDeleteAccountConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const tabs = [
    { id: 'account', label: 'Account', icon: '👤' },
    { id: 'preferences', label: 'Preferences', icon: '⚙️' },
    { id: 'privacy', label: 'Privacy', icon: '🔒' },
  ];

  const handleDeleteAccount = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/auth/account`, {
        method: 'DELETE',
        headers: {
          ...authUtils.getAuthHeader(),
        },
      });
      if (!response.ok) throw new Error('Failed to delete account');
      logout();
      window.location.href = '/';
    } catch (error) {
      console.error('Failed to delete account:', error);
      setMessage({ type: 'error', text: 'Failed to delete account. Please try again.' });
      setLoading(false);
      setDeleteAccountConfirm(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header - Title removed since TopBar already shows it */}
      <div className="mb-8">
        <p className="text-gray-600">Manage your account and preferences</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-8">
        <nav className="flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition
                ${activeTab === tab.id
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 sm:p-8">
        {/* Account Tab */}
        {activeTab === 'account' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-4">Account Information</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
                    {user?.email}
                  </div>
                  <p className="mt-1 text-xs text-gray-500">Your email address cannot be changed</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                  <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900">
                    {user?.name || 'Not set'}
                  </div>
                  <p className="mt-1 text-xs text-gray-500">Your display name</p>
                </div>

                {user?.email_verified && (
                  <div className="flex items-center gap-2 text-sm text-green-600">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Email verified</span>
                  </div>
                )}
              </div>
            </div>

            <div className="pt-6 border-t border-gray-200">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Danger Zone</h2>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h3 className="font-semibold text-red-900 mb-2">Delete Account</h3>
                <p className="text-sm text-red-700 mb-4">
                  Once you delete your account, there is no going back. Please be certain.
                </p>
                <button
                  onClick={() => setDeleteAccountConfirm(true)}
                  disabled={loading}
                  className="px-4 py-2 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition disabled:opacity-50"
                >
                  Delete Account
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Preferences Tab */}
        {activeTab === 'preferences' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-4">Preferences</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Theme</label>
                  <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-500">
                    Light (Coming soon)
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Language</label>
                  <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-500">
                    English (Coming soon)
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Privacy Tab */}
        {activeTab === 'privacy' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-4">Privacy & Data</h2>
              
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Data Export</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Download a copy of your data including all your plans and settings.
                  </p>
                  <button
                    disabled
                    className="px-4 py-2 bg-gray-100 text-gray-500 font-semibold rounded-lg cursor-not-allowed"
                  >
                    Export Data (Coming soon)
                  </button>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <h3 className="font-semibold text-gray-900 mb-2">Privacy Policy</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Read our privacy policy to understand how we handle your data.
                  </p>
                  <a
                    href="/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-purple-600 hover:text-purple-700 font-medium"
                  >
                    View Privacy Policy →
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Message */}
        {message.text && (
          <div className={`mt-4 p-3 rounded-lg ${
            message.type === 'error' ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600'
          }`}>
            {message.text}
          </div>
        )}
      </div>

      {/* Delete Account Confirmation */}
      <ConfirmationModal
        isOpen={deleteAccountConfirm}
        onClose={() => setDeleteAccountConfirm(false)}
        onConfirm={handleDeleteAccount}
        title="Delete Account?"
        message="This will permanently delete your account and all your plans. This action cannot be undone. Are you absolutely sure?"
        confirmText="Delete Account"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
};

export default SettingsPage;
